import React, { useCallback, useEffect, useMemo } from 'react'

import dayjs from 'dayjs'
import toast from 'react-hot-toast'
import { GrClose } from 'react-icons/gr'

import Classes from './Classes'
import PriceRow from './PriceRow'
import { Button, DateCalendar, Select } from '../..'
import {
  useCupons,
  useModal,
  useProfessors,
  useStudents,
} from '../../../context'
import { useBonus } from '../../../hooks/useBonusState'
import { NewCupon } from '../../../types'
import { Spacer } from '../../global'

import { ModalBackground, Modal, TitleRow, ModalRow, ButtonRow } from './styles'

const BonoModal = ({ info }: { info?: NewCupon | null | undefined }) => {
  const { studentsList, activeStudent } = useStudents()
  const { createCupon } = useCupons()
  const { professorsList } = useProfessors()
  const { modalStatus, setModalStatus } = useModal()

  const {
    newCupon,
    updateCupon,
    updateInput,
    handleStudentChange,
    updateStudent,
    updateDate,
    studentWithActiveCupon,
    resetCupon,
  } = useBonus()

  const isFormValid = useMemo(() => {
    return (
      newCupon.idStudent !== '' &&
      newCupon.amountOfClasses !== 0 &&
      newCupon.price !== 0 &&
      newCupon.initDate !== '' &&
      newCupon.dueDate !== ''
    )
  }, [newCupon])

  const handleClose = useCallback(() => {
    setModalStatus(!modalStatus)
    resetCupon()
  }, [modalStatus, setModalStatus])

  const isButtonDisable = useMemo(() => {
    return (
      newCupon.idStudent === '' ||
      newCupon.amountOfClasses === 0 ||
      newCupon.price === 0 ||
      newCupon.initDate === '' ||
      newCupon.dueDate === '' ||
      (newCupon.amountOfClasses === 1 && newCupon.teacher === '')
    )
  }, [newCupon])

  const handleSave = useCallback(async () => {
    if (!isFormValid) return

    try {
      await createCupon(newCupon)
      toast.success(info ? 'Bono actualizado' : 'Bono creado')
      resetCupon()
      handleClose()
    } catch (e) {
      console.error(e)
      toast.error('Hubo un error al guardar el bono.')
    }
  }, [isFormValid, createCupon, newCupon, resetCupon, handleClose, info])

  useEffect(() => {
    if (activeStudent) {
      updateStudent(activeStudent)
    }
  }, [])

  return (
    <ModalBackground>
      <Modal>
        <TitleRow>
          <div className="title">{info ? 'Editar Bono' : 'Agregar Bono'}</div>
          <div className="close" onClick={handleClose}>
            <GrClose />
          </div>
        </TitleRow>
        {studentsList && (
          <ModalRow>
            <Spacer height={20} />
            <div className="title">Alumno</div>
            <Spacer height={13} />

            <Select
              list={studentsList}
              value={newCupon.idStudent}
              name="idStudent"
              defaultValue="Selecciona un Alumno"
              handleSelectChange={handleStudentChange}
              showActiveBonus={true}
            />
          </ModalRow>
        )}
        <ModalRow>
          <div className="title">Tipo de Bono</div>
          <div className="menu">
            <Classes
              amountOfClasses={newCupon.amountOfClasses}
              handleMenuChange={updateCupon}
              studentHasActiveBonus={studentWithActiveCupon}
            />
          </div>
        </ModalRow>
        {newCupon.amountOfClasses === 1 && professorsList && (
          <ModalRow>
            <div className="title">Selecciona un profesor</div>
            <Spacer height={13} />
            <Select
              list={professorsList}
              value={newCupon.teacher}
              name="teacher"
              defaultValue="Selecciona un profesor"
              handleSelectChange={updateInput}
            />
          </ModalRow>
        )}
        <ModalRow className="price-row">
          <PriceRow
            price={newCupon.price}
            hasPayed={newCupon.payed}
            handleInputChange={updateInput}
            handleMenuChange={updateCupon}
          />
        </ModalRow>
        <ModalRow>
          <div className="title">Fecha de inicio</div>
          <Spacer height={18} />
          <div className="date-row">
            <DateCalendar
              referenceDate={newCupon.initDate}
              handleDate={updateDate}
              defaultValue={dayjs()}
              fullWidth={false}
            />

            {newCupon?.dueDate && (
              <div className="disclaimer">
                VENCE {dayjs(newCupon.dueDate).format('DD-MM-YYYY')}{' '}
              </div>
            )}
          </div>
          {newCupon.amountOfClasses > 1 && (
            <div className="subtitle">Duración 30 días desde el inicio.</div>
          )}
        </ModalRow>
        <ButtonRow>
          <Button
            disabled={isButtonDisable}
            onClick={handleSave}
            label="Guardar"
            className="dark-grey"
          />
        </ButtonRow>
      </Modal>
    </ModalBackground>
  )
}

export default BonoModal
